import React, { useCallback, useEffect, useState } from 'react';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import trackingService, {
  AllowlistEntry,
  AvailableCarrier,
  CarrierType,
} from '../../services/tracking.service';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

const CARRIER_TYPES: CarrierType[] = ['SCAC', 'RAIL'];

/**
 * Live-saving max-imports cell. Owns its own draft state so each row
 * can be edited independently. Flipping the toggle (Unlimited ↔ Cap at)
 * fires onSave immediately; typing a new number and tabbing out (or
 * pressing Enter) fires onSave on blur. No buttons, no edit mode.
 *
 * The toggle uses the same pill style as the Enabled/Disabled column
 * one cell over so the two interactions feel consistent.
 */
function MaxImportsCell({
  entry,
  onSave,
}: {
  entry: AllowlistEntry;
  onSave: (entry: AllowlistEntry, value: number | null) => Promise<void>;
}) {
  const [draft, setDraft] = useState<string>(
    entry.max_imports === null ? '' : String(entry.max_imports),
  );
  const [saving, setSaving] = useState(false);

  // Reset draft when the entry's saved value changes (after a successful
  // save the parent reloads; we want our draft to follow truth).
  useEffect(() => {
    setDraft(entry.max_imports === null ? '' : String(entry.max_imports));
  }, [entry.max_imports]);

  const isUnlimited = entry.max_imports === null;

  const flipMode = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (isUnlimited) {
        // Going Unlimited → Cap at. Use the draft if it's a valid
        // number, else default to 20 (matches the pilot baseline; admin
        // can immediately type to change it).
        const parsed = parseInt(draft.trim(), 10);
        const value = Number.isFinite(parsed) && parsed >= 0 ? parsed : 20;
        await onSave(entry, value);
      } else {
        // Going Cap at → Unlimited.
        await onSave(entry, null);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    if (saving) return;
    const trimmed = draft.trim();
    if (trimmed === '') return; // empty box → leave alone (toggle Unlimited if you want null)
    const parsed = parseInt(trimmed, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      // Snap the box back to the last-saved value rather than persisting garbage.
      setDraft(entry.max_imports === null ? '' : String(entry.max_imports));
      return;
    }
    if (parsed === entry.max_imports) return; // unchanged
    setSaving(true);
    try {
      await onSave(entry, parsed);
    } finally {
      setSaving(false);
    }
  };

  // Slider-style toggle. Off (left) shows "Unlimited"; On (right)
  // reveals an inline number input. Only one of the two is visible
  // at a time — the active control sits to the right of the switch.
  const isCapped = !isUnlimited;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={isCapped}
        aria-label={isCapped ? 'Capped — click to make unlimited' : 'Unlimited — click to set a cap'}
        onClick={flipMode}
        disabled={saving}
        title={isUnlimited ? 'Click to cap imports' : 'Click to make unlimited'}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
          isCapped
            ? 'bg-blue-600'
            : 'bg-gray-300 dark:bg-gray-600'
        } disabled:opacity-50 disabled:cursor-wait`}
      >
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${
            isCapped ? 'translate-x-[18px]' : 'translate-x-[2px]'
          }`}
        />
      </button>
      {isCapped ? (
        <input
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={saveDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur(); // triggers onBlur → save
            } else if (e.key === 'Escape') {
              setDraft(entry.max_imports === null ? '' : String(entry.max_imports));
              e.currentTarget.blur();
            }
          }}
          disabled={saving}
          className="w-16 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-2 py-0.5 text-xs"
        />
      ) : (
        <span className="text-xs font-medium text-muted italic select-none">
          Unlimited
        </span>
      )}
      {saving && (
        <span className="text-xs text-gray-400 dark:text-gray-500 italic">saving…</span>
      )}
    </div>
  );
}

export default function AllowedCarriers() {
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carrier catalog from gwiTrace — populates the Add dropdown.
  // Includes inactive carriers so admin can preemptively allowlist
  // them; gwiTrace handles the polling kill-switch on its side.
  const [availableCarriers, setAvailableCarriers] = useState<AvailableCarrier[]>([]);

  // Add-row form state. CapMode is a deliberate UI state so admins
  // pick "Unlimited" vs "Cap at N" explicitly rather than guessing
  // that a blank number input means unlimited.
  const [newType, setNewType] = useState<CarrierType>('SCAC');
  const [newCode, setNewCode] = useState('');
  const [newCapMode, setNewCapMode] = useState<'limited' | 'unlimited'>('limited');
  const [newMaxImports, setNewMaxImports] = useState<string>('20');
  const [adding, setAdding] = useState(false);

  // Inline max-imports edits use a live-save model: each row's Max
  // cell renders <MaxImportsCell/> below which owns its own draft +
  // saving state and calls back into `saveMaxImports` on blur / toggle.
  // No edit-mode, no check/cancel buttons — flip the toggle to switch
  // unlimited/cap, type a new number and tab away.

  // Delete confirmation state
  const [pendingDelete, setPendingDelete] = useState<AllowlistEntry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const {
    panelRef: deletePanelRef,
    panelStyle: deletePanelStyle,
    dragHandleProps: deleteDragHandleProps,
  } = useModalDrag(pendingDelete !== null);

  const load = useCallback(async () => {
    try {
      setError(null);
      // Fetch the allowlist and the carrier catalog in parallel — the
      // catalog feeds the Add dropdown; a failure to load it is
      // non-fatal (the dropdown shows empty and admin can still
      // toggle/delete existing rows).
      const [rows, available] = await Promise.all([
        trackingService.listAllowlist(),
        trackingService.listAvailableCarriers().catch(() => [] as AvailableCarrier[]),
      ]);
      setEntries(rows);
      setAvailableCarriers(available);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load allowlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const code = newCode.trim().toUpperCase();
    if (!code) {
      setError('Carrier code is required');
      return;
    }
    let maxImports: number | null = null;
    if (newCapMode === 'limited') {
      const parsed = parseInt(newMaxImports.trim(), 10);
      if (Number.isNaN(parsed) || parsed < 0) {
        setError('Cap must be a non-negative integer (or pick Unlimited)');
        return;
      }
      maxImports = parsed;
    }
    setAdding(true);
    try {
      setError(null);
      const created = await trackingService.createAllowlistEntry({
        carrier_type: newType,
        carrier_code: code,
        enabled: true,
        max_imports: maxImports,
      });
      setEntries((prev) => [...prev, created]);
      setNewCode('');
      setNewCapMode('limited');
      setNewMaxImports('20');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to add carrier');
    } finally {
      setAdding(false);
    }
  };

  const saveMaxImports = async (entry: AllowlistEntry, value: number | null) => {
    if (value === entry.max_imports) return; // no-op if unchanged
    try {
      setError(null);
      const updated = await trackingService.updateAllowlistEntry(entry.id, {
        max_imports: value,
      });
      // Optimistic local update — patching one row's max_imports doesn't
      // affect the catalog or any other row, so a full reload (which
      // would re-fetch the gwiTrace carrier catalog over the network)
      // is unnecessary and slow. Splice the updated row in place.
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? updated : e)));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update');
    }
  };

  const handleToggle = async (entry: AllowlistEntry) => {
    try {
      setError(null);
      const updated = await trackingService.updateAllowlistEntry(entry.id, {
        enabled: !entry.enabled,
      });
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? updated : e)));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update');
    }
  };

  const handleDelete = (entry: AllowlistEntry) => {
    setPendingDelete(entry);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      setError(null);
      const deletedId = pendingDelete.id;
      await trackingService.deleteAllowlistEntry(deletedId);
      setEntries((prev) => prev.filter((e) => e.id !== deletedId));
      setPendingDelete(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const grouped: Record<CarrierType, AllowlistEntry[]> = { SCAC: [], RAIL: [] };
  entries.forEach((e) => grouped[e.carrier_type].push(e));

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-body">Import Carriers</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Turns on <strong>IMPORT</strong> tracing per carrier &mdash; both registering orders and
          writing results back to CMS. Export is a separate switch on{' '}
          <strong>Export Carriers</strong>; enabling a carrier here does not turn on its exports,
          and disabling it here does not turn them off.
        </p>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Import tracing only runs for orders whose SCAC or rail code matches an enabled row below.
          Disabling a row stops new sweeps from picking up that carrier; existing tracing links
          remain in the database. <strong>Max imports</strong> picks the cap mode: <em>Unlimited</em>{' '}
          lets the register sweep create as many tracking links as match the carrier;{' '}
          <em>Cap at N</em> stops at N active links (a pilot/safety lever). Changes apply within{' '}
          ~60 seconds (cache TTL).
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-3 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="mb-6 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-sm font-medium text-body mb-3">Add a carrier</h2>
        <div className="flex gap-2 items-end">
          <div>
            <label className="block text-xs font-medium text-label mb-1">
              Type
            </label>
            <select
              value={newType}
              onChange={(e) => {
                setNewType(e.target.value as CarrierType);
                // Clear the previously-selected code — switching Type
                // means the catalog options shift, so the old selection
                // is almost certainly no longer valid.
                setNewCode('');
              }}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-3 py-1.5 text-sm"
            >
              {CARRIER_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-label mb-1">
              Code
            </label>
            {(() => {
              // Filter the catalog to carriers matching the chosen Type
              // (SCAC ↔ steamship_line, RAIL ↔ rail+terminal), and drop
              // any code that's already in the allowlist so we never
              // offer a duplicate.
              const existingCodes = new Set(
                entries
                  .filter((e) => e.carrier_type === newType)
                  .map((e) => e.carrier_code.toUpperCase()),
              );
              const options = availableCarriers
                .filter((c) => c.carrier_type === newType)
                .filter((c) => !existingCodes.has(c.code.toUpperCase()))
                .sort((a, b) => a.code.localeCompare(b.code));
              return (
                <select
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-3 py-1.5 text-sm"
                >
                  <option value="">
                    {options.length === 0
                      ? availableCarriers.length === 0
                        ? '— catalog unavailable —'
                        : '— all carriers already added —'
                      : '— select a carrier —'}
                  </option>
                  {options.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.name}
                      {!c.is_active ? ' (inactive in gwiTrace)' : ''}
                    </option>
                  ))}
                </select>
              );
            })()}
          </div>
          <div className="w-44">
            <label className="block text-xs font-medium text-label mb-1">
              Max imports
            </label>
            <div className="flex items-center gap-2 h-[34px]">
              <button
                type="button"
                role="switch"
                aria-checked={newCapMode === 'limited'}
                aria-label={newCapMode === 'limited' ? 'Capped — click to make unlimited' : 'Unlimited — click to set a cap'}
                onClick={() => setNewCapMode(newCapMode === 'limited' ? 'unlimited' : 'limited')}
                title={newCapMode === 'limited' ? 'Click to make unlimited' : 'Click to cap imports'}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                  newCapMode === 'limited'
                    ? 'bg-blue-600'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${
                    newCapMode === 'limited' ? 'translate-x-[18px]' : 'translate-x-[2px]'
                  }`}
                />
              </button>
              {newCapMode === 'limited' ? (
                <input
                  type="text"
                  inputMode="numeric"
                  value={newMaxImports}
                  onChange={(e) => setNewMaxImports(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="e.g. 20"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                  className="w-20 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-2 py-1.5 text-sm"
                />
              ) : (
                <span className="text-sm font-medium text-muted italic select-none">
                  Unlimited
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleAdd}
            disabled={adding || !newCode.trim()}
            className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
      ) : (
        CARRIER_TYPES.map((type) => (
          <div key={type} className="mb-6">
            <h2 className="text-lg font-semibold text-body mb-2">
              {type === 'SCAC' ? 'Steamship Lines (SCAC)' : 'Rail Carriers'}
            </h2>
            {grouped[type].length === 0 ? (
              <p className="text-sm text-muted italic">None configured.</p>
            ) : (
              <div className="overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">
                        Code
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">
                        Max
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                    {grouped[type].map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-4 py-2 text-sm text-body">
                          {entry.carrier_code}
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleToggle(entry)}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              entry.enabled
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {entry.enabled ? 'Enabled' : 'Disabled'}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-sm text-body">
                          <MaxImportsCell entry={entry} onSave={saveMaxImports} />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => handleDelete(entry)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                            title="Remove from allowlist"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}

      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50"
          onClick={() => !deleting && setPendingDelete(null)}
        >
          <div
            ref={deletePanelRef}
            style={deletePanelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title={
                <span className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
                  Remove from allowlist
                </span>
              }
              onClose={deleting ? undefined : () => setPendingDelete(null)}
              dragHandleProps={deleteDragHandleProps}
            />

            <div className="px-6 py-4">
              <p className="text-label">
                Remove <span className="font-semibold">{pendingDelete.carrier_type} {pendingDelete.carrier_code}</span> from the allowlist?
              </p>
              <p className="mt-2 text-sm text-muted">
                Existing tracking links for this carrier will remain in the database; only new sweeps stop picking it up.
                You can re-add the carrier later if needed.
              </p>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setPendingDelete(null)}
                className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={deleting}
              >
                {deleting && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                {deleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
